import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/PageHeader';
import { PREMIUM_PRICING } from '@/config/pricingConfig';
import LaunchOfferBadge from '@/components/LaunchOfferBadge';

const PremiumUpsellBreathing = ({ t }) => {
  const navigate = useNavigate();
  const isEnglish = t?.locale === 'en-US';

  const features = [
    {
      fr: '8 techniques de respiration avancées',
      en: '8 advanced breathing techniques',
    },
    {
      fr: '4 durées configurables (1, 3, 5, 10 min)',
      en: '4 configurable durations (1, 3, 5, 10 min)',
    },
    {
      fr: 'Animations premium synchronisées',
      en: 'Synchronized premium animations',
    },
    {
      fr: 'Mode audio guidé (à venir)',
      en: 'Guided audio mode (coming soon)',
    },
    {
      fr: 'Programmes d\'apaisement personnalisés',
      en: 'Personalized calming programs',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C] px-4 md:px-8 pb-32 relative overflow-hidden" style={{ paddingTop: '100px' }}>
      {/* Background effects */}
      <div className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-2xl mx-auto relative z-10">
        <PageHeader t={t} />

        <div className="mt-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#6F42EA] to-[#441A7A] flex items-center justify-center shadow-[0_0_40px_rgba(111,66,234,0.3)]">
              <span className="text-5xl">🫁</span>
            </div>
          </div>

          {/* Glass card */}
          <div
            className="rounded-3xl p-8 backdrop-blur-md border"
            style={{
              background: 'linear-gradient(135deg, rgba(111, 66, 234, 0.1) 0%, rgba(68, 26, 122, 0.05) 100%)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Title */}
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-white mb-3">
                {isEnglish ? 'Unlock Premium Breathing' : 'Débloquez la Respiration Premium'}
              </h1>
              <p className="text-white/70 text-base">
                {isEnglish
                  ? 'Access 8 advanced techniques, 4 durations, premium animations, guided audio mode and personalized calming programs.'
                  : 'Accédez à 8 techniques avancées, 4 durées, animations premium, mode audio guidé et programmes d\'apaisement personnalisés.'}
              </p>
            </div>

            {/* Features list */}
            <div className="space-y-4 mb-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#6F42EA]/20 flex items-center justify-center mt-0.5">
                    <Check className="w-4 h-4 text-[#6F42EA]" />
                  </div>
                  <span className="text-white/90 text-sm">{isEnglish ? feature.en : feature.fr}</span>
                </div>
              ))}
            </div>

            {/* Launch Offer */}
            <div className="mb-6">
              <LaunchOfferBadge 
                isEnglish={isEnglish} 
                variant="compact"
              />
            </div>

            {/* Pricing info */}
            <div className="text-center mb-8 py-4 border-t border-b border-white/10">
              <p className="text-white/60 text-xs mb-2">
                {isEnglish ? 'Starting from' : 'À partir de'}
              </p>
              {isEnglish ? (
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl font-bold text-white">
                    {PREMIUM_PRICING.monthly.priceEn.split('.')[0]}
                  </span>
                  <span className="text-2xl font-bold text-white">
                    .{PREMIUM_PRICING.monthly.priceEn.split('.')[1]}
                  </span>
                </div>
              ) : (
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl font-bold text-white">
                    {PREMIUM_PRICING.monthly.price.split(',')[0]}
                  </span>
                  <span className="text-2xl font-bold text-white">
                    ,{PREMIUM_PRICING.monthly.price.split(',')[1]}
                  </span>
                </div>
              )}
              <p className="text-white/70 text-sm mt-1">
                {isEnglish ? PREMIUM_PRICING.monthly.periodEn : PREMIUM_PRICING.monthly.period}
              </p>
            </div>

            {/* CTA Button */}
            <Button
              onClick={() => navigate('/premium')}
              className="w-full bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] hover:from-[#5F32DA] hover:to-[#7B4CE6] text-white font-semibold py-6 rounded-xl transition-all shadow-[0_4px_20px_rgba(111,66,234,0.4)] hover:shadow-[0_6px_30px_rgba(111,66,234,0.6)] text-base"
            >
              <Crown className="w-5 h-5 mr-2" />
              {isEnglish ? 'Upgrade to Hearen Premium' : 'Passer à Hearen Premium'}
            </Button>
          </div>

          {/* Back button */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(-1)}
              className="text-white/60 hover:text-white/90 text-sm transition-colors"
            >
              {isEnglish ? '← Back' : '← Retour'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpsellBreathing;
